import { useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { MilestoneTask } from "../../../../../../../../../api/homeSiteMilestones/types";
import { CombinedMilestoneTask } from "../types";
import { useCreateMilestoneTask } from "../../../../../../../../../reactQuery/homeSites/milestones/mileStoneTasksQuery";

interface UseMilestoneTaskMenuProps {
     userId: string;
     updateMilestoneTasks: (data: any) => void;
     saleDate: string | null | undefined;
     closeOfEscrowActual: string | null | undefined;
     closeOfEscrowEstimated: string | null | undefined;
     homeSiteId: string;
     divisionCode: string;
}

export const useMilestoneTaskMenu = ({
     userId,
     updateMilestoneTasks,
     saleDate,
     closeOfEscrowActual,
     closeOfEscrowEstimated,
     homeSiteId,
     divisionCode
}: UseMilestoneTaskMenuProps) => {
     // ============================================================================
     // STATE
     // ============================================================================

     const [taskMenuAnchorEl, setTaskMenuAnchorEl] = useState<{ [key: string]: HTMLElement | null; }>({});
     const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
     const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
     const [selectedTask, setSelectedTask] = useState<CombinedMilestoneTask | null>(null);

     // ============================================================================
     // MUTATIONS
     // ============================================================================

     const createMilestoneTaskMutation = useCreateMilestoneTask(divisionCode);

     // ============================================================================
     // HELPER FUNCTIONS
     // ============================================================================

     const createNewTaskPayload = (task: CombinedMilestoneTask): Partial<MilestoneTask> => ({
          divisionCode: task.divisionTemplate.divisionCode,
          taskName: task.divisionTemplate.taskName,
          homeSiteId: homeSiteId,
          mileStoneName: task.divisionTemplate.mileStoneName,
          conciergeEmail: '',
          saleDate: saleDate,
          closeOfEscrowActual: closeOfEscrowActual,
          closeOfEscrowEstimated: closeOfEscrowEstimated,
          createdBy: userId,
          updatedBy: userId, // Add updatedBy for consistency
          taskCode: task.divisionTemplate.taskCode,
     });

     // ============================================================================
     // EVENT HANDLERS
     // ============================================================================

     const handleTaskMenuOpen = (event: React.MouseEvent<HTMLElement>, taskName: string, tasks: CombinedMilestoneTask[]) => {
          setTaskMenuAnchorEl((prev) => ({
               ...prev,
               [taskName]: event.currentTarget,
          }));
          setSelectedTask(tasks.find((task) => task.divisionTemplate.taskName === taskName) || null);
     };

     const handleTaskMenuClose = (taskName: string) => {
          setTaskMenuAnchorEl((prev) => ({ ...prev, [taskName]: null }));
          setSelectedTask(null);
          setIsDatePickerOpen(false);
     };

     const handleDateChange = (newDate: Dayjs | null, selectionState: any | undefined) => {
          setSelectedDate(newDate);

          if (selectedTask && newDate && selectionState === "finish") {
               if (selectedTask.milestoneTaskData?.id) {
                    // Update existing task
                    const payload: Partial<MilestoneTask> = {
                         id: selectedTask.milestoneTaskData.id,
                         homeSiteId: selectedTask.milestoneTaskData.homeSiteId,
                         divisionCode: selectedTask.milestoneTaskData.divisionCode,
                         mileStoneName: selectedTask.milestoneTaskData.mileStoneName,
                         updatedBy: userId,
                         dueDate: newDate.toDate(),
                         saleDate,
                         closeOfEscrowActual,
                         closeOfEscrowEstimated,
                    };

                    updateMilestoneTasks([payload]);
               } else {
                    // Create new task when no existing record is present
                    const newTaskPayload = createNewTaskPayload(selectedTask);
                    newTaskPayload.dueDate = newDate.toDate();
                    newTaskPayload.updatedBy = userId;

                    createMilestoneTaskMutation.mutate(newTaskPayload);
               }

               setIsDatePickerOpen(false);
               setSelectedTask(null);
               if (selectedTask?.divisionTemplate.taskName) {
                    handleTaskMenuClose(selectedTask.divisionTemplate.taskName);
               }
          }
     };

     const handleMenuAction = (action: string, task: CombinedMilestoneTask) => {
          switch (action) {
               case "Change Due Date":
                    setSelectedTask(task);
                    setSelectedDate(task.milestoneTaskData?.dueDate ? dayjs(task.milestoneTaskData?.dueDate) : null);
                    setIsDatePickerOpen(true);
                    break;
               case "Not Applicable":
                    if (task.milestoneTaskData?.id) {
                         // Update existing task
                         const payload: Partial<MilestoneTask> = {
                              id: task.milestoneTaskData.id,
                              homeSiteId: task.milestoneTaskData.homeSiteId,
                              divisionCode: task.milestoneTaskData.divisionCode,
                              mileStoneName: task.milestoneTaskData.mileStoneName,
                              updatedBy: userId,
                              completionDate: task.milestoneTaskData?.status === false ? null : new Date(),
                              status: task.milestoneTaskData?.status === false ? null : false,
                              saleDate,
                              closeOfEscrowActual,
                              closeOfEscrowEstimated,
                         };
                         updateMilestoneTasks([payload]);
                    } else {
                         // Create new task when no existing record is present
                         const newTaskPayload = createNewTaskPayload(task);
                         newTaskPayload.status = false;
                         newTaskPayload.completionDate = new Date();
                         newTaskPayload.updatedBy = userId;

                         createMilestoneTaskMutation.mutate(newTaskPayload);
                    }
                    handleTaskMenuClose(task.divisionTemplate.taskName);
                    break;
               default:
                    break;
          }
     };

     // ============================================================================
     // RETURN OBJECT
     // ============================================================================

     return {
          taskMenuAnchorEl,
          isDatePickerOpen,
          selectedDate,
          selectedTask,
          handleTaskMenuOpen,
          handleTaskMenuClose,
          handleDateChange,
          handleMenuAction,
     };
}; 